import { useUserProfile } from '@/context/UserProfileContext';
import { api } from '@/utils/api';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonLabel, IonModal, IonSegment, IonSegmentButton, IonTitle, IonToolbar, useIonRouter, useIonToast } from '@ionic/react';
import { chevronBack, chevronForward, closeOutline, documentTextOutline } from 'ionicons/icons';
import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import GolfIcon from '../GolfIcon';
import GameSettingsContent from './GameSettingsContent';
import InGameScorecard from './InGameScorecard';
import type { PlayerInfo } from './types';
import { comment } from 'postcss';

interface ScoreDetails {
  score: number | null;
  putts: number | null;
}

interface ExtendedPlayerInfo extends PlayerInfo {
  status?: string;
  avatar?: string;
  scores?: { [holeNumber: number]: ScoreDetails };
}

interface HoleSpecificData {
  par?: number;
  handicap?: number;
}

interface HoleInformationProps {
  activeHole: number;
  holeData: HoleSpecificData[];
  gameId: string;
  onQuitGame: () => void;
  onDeleteGame: () => void;
  onFinishGame: () => void;
  nextHole: () => void;
  previousHole: () => void;
  players?: ExtendedPlayerInfo[];
}

export const HoleInformation = ({
  activeHole,
  holeData,
  gameId,
  onQuitGame,
  onDeleteGame,
  onFinishGame,
  nextHole,
  previousHole,
  players = []
}: HoleInformationProps) => {
  const { t } = useTranslation();
  const ionRouter = useIonRouter();
  const [presentToast] = useIonToast();
  const { profile } = useUserProfile();

  // State variables
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<string>("");
  const [localPlayers, setLocalPlayers] = useState<ExtendedPlayerInfo[]>(players);
  const [showScorecardModal, setShowScorecardModal] = useState<boolean>(false);
  const [isGameSettingsModalOpen, setIsGameSettingsModalOpen] = useState<boolean>(false);
  const [activeScorecardPlayer, setActiveScorecardPlayer] = useState<ExtendedPlayerInfo | null>(null);
  const [publishToFeed, setPublishToFeed] = useState<boolean>(true);

  // Reference to the parent modal
  const modal = useRef<HTMLIonModalElement>(null);

  // Haptic feedback function
  const mediumImpact = async () => {
    await Haptics.impact({ style: ImpactStyle.Medium });
  };

  // Fetch current user and game info on component mount
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        // Get current user info
        setCurrentUser(profile?.userId || '');

        // Get game info to check admin status
        const gameInfo = await api.get(`/game/${gameId}`);
        setIsAdmin(gameInfo.data.createdBy === profile?.userId);
      } catch (error) {
        console.error('Error fetching user or game info:', error);
      }
    };

    fetchUserInfo();
  }, [gameId, profile]);

  // Update local players state when props change
  useEffect(() => {
    setLocalPlayers(players);
    if (players.length > 0 && !activeScorecardPlayer) {
      setActiveScorecardPlayer(players[0]);
    }
  }, [players, activeScorecardPlayer]);

  // Function for a player to mark themselves as finished
  const markPlayerAsFinished = async () => {
    try {
      // Use the specific endpoint for marking a player as finished
      await api.post(`/game/${gameId}/player/finish`, {
        publishToFeed: publishToFeed,
      });

      // Update local state
      setLocalPlayers(localPlayers.map(player => {
        if (player.userId === currentUser) {
          return { ...player, status: 'finished' };
        }
        return player;
      }));

      presentToast({
        message: t('round_finished'),
        duration: 2000,
        position: 'bottom',
        color: 'success'
      });

      onFinishGame();
      setShowScorecardModal(false);
    } catch (error) {
      console.error('Error marking player as finished:', error);
      presentToast({
        message: t('error_finishing_round'),
        duration: 2000,
        position: 'bottom',
        color: 'danger'
      });
    }
  };

  // Determine if current player is already finished
  const isPlayerFinished = localPlayers.some(player => player.userId === currentUser && player.status === 'finished');

  return (
    <div className="pb-safe">
      {/* Minimal view (when at 0.1 breakpoint) */}
      <div className="px-2">
        <div className="flex items-center justify-between mx-auto pt-3">
          <button
            onClick={previousHole}
            disabled={activeHole <= 1}
            className={`p-2 rounded-full ${activeHole <= 1 ? 'text-muted-foreground' : 'text-foreground'}`}
          >
            <IonIcon icon={chevronBack} className="text-xl" />
          </button>

          <div className="flex items-center justify-center cursor-pointer">
            <GolfIcon name="flag-filled" themeAware={false} className='size-6 mr-1' />
            <span className="text-4xl font-extrabold mr-2">{String(activeHole).padStart(2, '0')}</span>
            <div className="flex flex-col justify-center">
              <span className="text-xs text-muted-foreground font-bold">PAR {holeData[activeHole - 1]?.par}</span>
              <span className="text-xs text-muted-foreground font-bold">HCP {holeData[activeHole - 1]?.handicap}</span>
            </div>
          </div>

          <button
            onClick={nextHole}
            disabled={activeHole >= holeData.length}
            className={`p-1 rounded-full ${activeHole >= holeData.length ? 'text-muted-foreground' : 'text-foreground'}`}
          >
            <IonIcon icon={chevronForward} className="text-xl" />
          </button>
        </div>
      </div>

      {/* Expanded view content (when modal is pulled up) */}
      <div className="mt-6 p-2">
        <div className="space-y-4">
          <div className="flex justify-between items-center gap-2">
            <button
              onClick={() => {
                setIsGameSettingsModalOpen(true);
                // Close the HoleInformation modal when opening game settings
                if (modal.current) {
                  modal.current.dismiss();
                }
              }}
              className="flex-1 flex flex-col items-center justify-center"
            >
              <div className="bg-card border-input border rounded-xl w-16 h-16 flex items-center justify-center mb-1">
                <GolfIcon name="settings" className="text-primary text-2xl" />
              </div>
              <span className="text-xs text-foreground font-medium">{t('settings')}</span>
            </button>
            <button
              onClick={() => ionRouter.push('/my-bag')}
              className="flex-1 flex flex-col items-center justify-center"
            >
              <div className="bg-card border-input border rounded-xl w-16 h-16 flex items-center justify-center mb-1">
                <GolfIcon name="golf-bag" className="text-primary text-2xl" />
              </div>
              <span className="text-xs text-foreground font-medium">{t('Mon Sac')}</span>
            </button>
            {/* <button
              onClick={() => ionRouter.push('/messages')}
              className="flex-1 flex flex-col items-center justify-center"
            >
              <div className="bg-card border-input border rounded-xl w-16 h-16 flex items-center justify-center mb-1">
                <GolfIcon name="message-multiple" className="text-primary text-2xl" />
              </div>
              <span className="text-xs text-foreground font-medium">{t('chat')}</span>
            </button> */}
            <button
              onClick={() => {
                setShowScorecardModal(true);
                // Close the HoleInformation modal when opening scorecard
                if (modal.current) {
                  modal.current.dismiss();
                }
              }}
              className="flex-1 flex flex-col items-center justify-center"
            >
              <div className="bg-card border-input border rounded-xl w-16 h-16 flex items-center justify-center mb-1">
                <IonIcon icon={documentTextOutline} className="text-primary text-2xl" />
              </div>
              <span className="text-xs text-foreground font-medium">{t('scorecard')}</span>
            </button>
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-2 pt-8">
            <IonButton color="secondary" expand="block" fill="solid" className="rounded-xl" onClick={onQuitGame}>
              {t('quit_game_screen')}
            </IonButton>

            {!isPlayerFinished ? (
              <IonButton expand="block" color="primary" className="rounded-xl" onClick={() => {
                setShowScorecardModal(true);
                // Close the HoleInformation modal when opening scorecard
                if (modal.current) {
                  modal.current.dismiss();
                }
              }}>
                {t('finish_game')}
              </IonButton>
            ) : (
              <div className="rounded-xl border border-green-600 text-green-600 p-3 text-center font-medium bg-green-50 dark:bg-green-900/20">
                {t('you_have_finished')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scorecard Modal */}
      <IonModal isOpen={showScorecardModal} onDidDismiss={() => setShowScorecardModal(false)} className="scorecard-modal">
        <IonHeader>
          <IonToolbar color="dark">
            <IonTitle>{t('scorecard')}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowScorecardModal(false)}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {/* Player Switcher */}
          {localPlayers && localPlayers.length > 1 && (
            <div className="p-2">
              <IonSegment value={activeScorecardPlayer?.id} onIonChange={(e) => {
                const selectedPlayerId = e.detail.value;
                const selectedPlayer = localPlayers.find(p => p.id === selectedPlayerId);
                if (selectedPlayer) {
                  setActiveScorecardPlayer(selectedPlayer);
                }
              }}>
                {localPlayers.map(player => (
                  <IonSegmentButton key={player.id} value={player.id}>
                    <div className="flex flex-col items-center">
                      <img
                        src={player.avatar || '/assets/images/fallback.jpg'}
                        alt={player.name}
                        className="size-8 rounded-full object-cover mb-1"
                      />
                      <IonLabel className="text-xs">{player.name}</IonLabel>
                    </div>
                  </IonSegmentButton>
                ))}
              </IonSegment>
            </div>
          )}

          {/* InGameScorecard component */}
          {activeScorecardPlayer && (
            <div className="p-2">
              <InGameScorecard
                gameId={gameId}
                playerId={activeScorecardPlayer.userId}
                playerName={activeScorecardPlayer.name}
              />
            </div>
          )}
        </IonContent>
      </IonModal>

      {/* Game Settings Modal */}
      <IonModal isOpen={isGameSettingsModalOpen} onDidDismiss={() => setIsGameSettingsModalOpen(false)}>
        <GameSettingsContent gameId={gameId} onClose={() => setIsGameSettingsModalOpen(false)} />
      </IonModal>
    </div>
  );
};
